import type { GalleryCommercePolicy } from '@/lib/gallery-access-rules'
import type { OriginalDownloadMode, Photo } from '@/lib/photos'
import { getDownloadUrl } from '@/lib/download-url'

/**
 * Client-safe helpers for My Selection / saved favorites. Policies always come
 * from the server (`/api/selection/policy` or a server component); anything
 * missing or unavailable is treated as not purchasable and not a free original.
 */

export const MAX_SELECTION_POLICY_ITEMS = 200

const FAVORITE_LIST_TOKEN_PATTERN = /^[A-Za-z0-9_-]{20,128}$/

export type SelectedPhoto = Photo & {
  /** Share-link token of the private gallery the photo was selected from. */
  shareToken?: string
  /** Token of the saved favorites list the photo was opened from (opens only that list's photos). */
  favoriteToken?: string
}

/** A saved favorites-list token, or null when missing/malformed. */
export function normalizeFavoriteListToken(value: unknown) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return FAVORITE_LIST_TOKEN_PATTERN.test(trimmed) ? trimmed : null
}

export type SelectionPhotoPolicy =
  | {
      status: 'ok'
      freeOriginal: boolean
      originalDownloadMode: OriginalDownloadMode
      unwatermarkedOriginal: boolean
      watermarkedDownloads: boolean
      /** Already includes the site-wide ecommerce switch. */
      purchasable: boolean
      proofingNotes: boolean
    }
  | { status: 'unavailable' }

export type SelectionPolicyMap = Record<string, SelectionPhotoPolicy>

export function toSelectionPhotoPolicy(
  result: { status: 'ok'; policy: GalleryCommercePolicy } | { status: 'unavailable' },
  ecommerceEnabled: boolean
): SelectionPhotoPolicy {
  if (result.status !== 'ok') return { status: 'unavailable' }

  const { policy } = result
  return {
    status: 'ok',
    freeOriginal: policy.freeOriginal,
    originalDownloadMode: policy.originalDownloadMode,
    unwatermarkedOriginal: policy.unwatermarkedOriginal,
    watermarkedDownloads: policy.watermarkedDownloads,
    purchasable: ecommerceEnabled && policy.purchasable,
    proofingNotes: policy.proofingNotes,
  }
}

/**
 * The gallery share token for a selected photo. Older selections didn't store it,
 * but private-gallery photos carry it in their image URLs (`?share=`).
 */
export function getSelectionShareToken(photo: Partial<SelectedPhoto>): string | undefined {
  if (typeof photo.shareToken === 'string' && photo.shareToken.trim()) return photo.shareToken.trim()
  return getUrlParam(photo, 'share')
}

/**
 * The favorites-list token for a photo opened from a saved list, stored on the
 * selection or carried in its image URLs (`?fav=`).
 */
export function getSelectionFavoriteToken(photo: Partial<SelectedPhoto>): string | undefined {
  return normalizeFavoriteListToken(photo.favoriteToken)
    ?? normalizeFavoriteListToken(getUrlParam(photo, 'fav'))
    ?? undefined
}

function getUrlParam(photo: Partial<SelectedPhoto>, name: string) {
  for (const url of [photo.thumbnailSrc, photo.src]) {
    if (typeof url !== 'string' || !url.includes(`${name}=`)) continue
    try {
      const value = new URL(url, 'http://selection.local').searchParams.get(name)?.trim()
      if (value) return value
    } catch {
      // Ignore malformed URLs.
    }
  }

  return undefined
}

export function getPhotoPolicy(policies: SelectionPolicyMap | null | undefined, photo: Pick<Photo, 'filename'>) {
  const policy = policies?.[photo.filename]
  return policy?.status === 'ok' ? policy : null
}

export interface SelectionSummary<T extends Pick<Photo, 'filename'>> {
  /** Photos that can be bought as high-res files or prints. */
  purchasable: T[]
  /** Photos whose gallery gives high-res files away. */
  freeOriginals: T[]
  /** Free originals delivered without a watermark. */
  unwatermarkedOriginals: T[]
  /** Photos that should offer per-photo proofing notes. */
  notePhotos: T[]
  /** True when no photo in the selection takes proofing notes. */
  hideProofing: boolean
  highResTotalCents: number
}

export function summarizeSelection<T extends Pick<Photo, 'filename'>>(
  photos: T[],
  policies: SelectionPolicyMap | null | undefined,
  priceCents: number
): SelectionSummary<T> {
  const purchasable: T[] = []
  const freeOriginals: T[] = []
  const unwatermarkedOriginals: T[] = []
  const notePhotos: T[] = []

  for (const photo of photos) {
    const policy = getPhotoPolicy(policies, photo)
    if (policy?.purchasable) purchasable.push(photo)
    if (policy?.freeOriginal) freeOriginals.push(photo)
    if (policy?.unwatermarkedOriginal) unwatermarkedOriginals.push(photo)
    // Unknown policy keeps notes available; commerce stays closed.
    if (!policy || policy.proofingNotes) notePhotos.push(photo)
  }

  return {
    purchasable,
    freeOriginals,
    unwatermarkedOriginals,
    notePhotos,
    hideProofing: photos.length > 0 && notePhotos.length === 0,
    highResTotalCents: purchasable.length * priceCents,
  }
}

/** The download URL for one photo in "Download all", or null when it can't be downloaded. */
export function getSelectionDownloadUrl(
  photo: Pick<SelectedPhoto, 'filename' | 'shareToken' | 'favoriteToken' | 'src' | 'thumbnailSrc'>,
  policies: SelectionPolicyMap | null | undefined,
  options: { freeDownloadMaxWidth: number; downloadVersion: string }
) {
  const share = getSelectionShareToken(photo)
  const fav = getSelectionFavoriteToken(photo)
  const policy = getPhotoPolicy(policies, photo)

  if (policy?.freeOriginal) {
    return getDownloadUrl(photo.filename, { variant: 'original', share, fav })
  }

  if (policy && !policy.watermarkedDownloads) return null

  return getDownloadUrl(photo.filename, {
    w: String(options.freeDownloadMaxWidth),
    v: options.downloadVersion,
    share,
    fav,
  })
}
